import React, { useState, useEffect } from 'react';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { usePageTitle } from '../hooks/usePageTitle';
import { trackFaqClick } from '../utils/analytics';
import { APP_NAME } from '../config/branding';

const FAQ: React.FC = () => {
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  
  usePageTitle('FAQ - Frequently Asked Questions About AI Veterinary Phone Agent');
  
  useEffect(() => {
    // Track FAQ page view
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_title: 'FAQ Page',
        page_location: window.location.href,
        page_path: '/faq',
        event_category: 'page_view'
      });
    }
  }, []);

  const faqs = [
    {
      question: 'Can the AI handle emergency calls?',
      answer: `Yes, ${APP_NAME} can identify emergency situations and immediately transfer calls to your emergency line or on-call veterinarian. You can customize how emergency calls are handled based on your practice's protocols.`
    },
    {
      question: 'Will the AI Front Desk replace the receptionist?',
      answer: 'No, the AI Front Desk is designed to work alongside your receptionist, freeing them up to focus on in-person patient care while AI handles routine scheduling and questions. It does not handle emergency calls or in-person patient care.'
    },
    {
      question: `How much does ${APP_NAME} cost?`,
      answer: 'See our Pricing page for plan details (Essential, Professional, and Enterprise).'
    },
    {
      question: `How long does it take to set up ${APP_NAME}?`,
      answer: 'Most practices are up and running in under 5 minutes. Simply sign up, connect your phone number and calendar, and the AI starts answering calls immediately. No technical expertise required.'
    },
    {
      question: 'What happens if the AI doesn\'t understand a caller?',
      answer: 'If the AI encounters a situation it can\'t handle, it seamlessly transfers the call to your staff with full context of what the caller needs. You can also customize which types of calls get transferred automatically.'
    },
    {
      question: 'Are there any monthly minimums or contracts?',
      answer: `No! ${APP_NAME} has no monthly minimums and does not require long-term contracts. You only pay for what you use. Cancel anytime with no penalties.`
    },
    {
      question: 'What payment methods do you accept?',
      answer: 'We accept all major credit cards (Visa, Mastercard, American Express, Discover) and ACH bank transfers. You\'ll be billed monthly for your usage.'
    },
    {
      question: 'Is there a free trial?',
      answer: 'Credit card required to get started. You can cancel anytime with no penalties or long-term commitments.'
    },
    {
      question: 'What\'s included in the Enterprise plan?',
      answer: 'Enterprise pricing is for multi-location veterinary groups with high volume needs. It includes volume discounts, dedicated support, custom integrations, and priority feature requests. Contact us for a custom quote.'
    },
    {
      question: 'How does the AI Front Desk Agent handle phone calls?',
      answer: 'Our AI agent answers calls 24/7, handles appointment scheduling, basic triage questions, and routes urgent cases to appropriate staff - all while integrating with your existing practice management system.'
    },
    {
      question: 'Can the system really save 80% of phone time?',
      answer: 'Yes! By automating routine calls, appointment scheduling, prescription refill requests, and basic questions, your staff can focus on patient care instead of phone management.'
    },
    {
      question: 'What happens if the call requires a human touch or urgent care?',
      answer: 'Every call has a built-in emergency hatch. If a call requires a human touch, it seamlessly transfers to your team with full context of the conversation, ensuring safety, trust, and the best possible experience for pet owners.'
    },
    {
      question: 'Is the AI Front Desk Agent secure and compliant?',
      answer: 'Absolutely. All voice data is encrypted and transmitted to secure cloud storage. Only authorized practice staff have access to patient information.'
    },
    {
      question: 'Does it integrate with existing calendar systems?',
      answer: 'Yes, our calendar automation works with major practice management systems to automatically schedule appointments, send reminders, and manage follow-up care based on AI-analyzed visit notes.'
    },
    {
      question: 'What languages does the AI support?',
      answer: 'Currently, the AI Front Desk Agent supports English. We are working on adding Spanish and other languages based on customer demand.'
    },
    {
      question: 'Can I customize the AI\'s responses?',
      answer: 'Yes! You can customize greetings, hours of operation, emergency protocols, appointment types, and more to match your practice\'s specific needs and voice.'
    },
    {
      question: 'How accurate is the AI in understanding callers?',
      answer: 'Our AI uses advanced speech recognition and natural language processing with over 95% accuracy in understanding caller intent. It continuously learns and improves from each interaction.'
    },
    {
      question: 'What happens if my practice is closed?',
      answer: 'The AI can provide after-hours information, take messages, handle emergency routing to on-call vets, and schedule appointments for the next available time slot.'
    },
    {
      question: 'Can the AI handle multiple calls at once?',
      answer: 'Yes! Unlike human staff, the AI can handle unlimited simultaneous calls, ensuring no caller ever gets a busy signal or has to wait on hold.'
    },
    {
      question: 'How does billing work?',
      answer: 'Billing is based on your selected plan. See the Pricing page for what’s included and overage details.'
    },
    {
      question: 'Can I cancel anytime?',
      answer: 'Absolutely. There are no contracts or cancellation fees. You can pause or cancel your service at any time with no penalties.'
    },
    {
      question: 'Do you offer training or support?',
      answer: 'Yes! We provide onboarding assistance, training materials, and ongoing customer support via email and phone. Our team is here to help you succeed.'
    },
    {
      question: 'What kind of analytics do you provide?',
      answer: 'You get detailed analytics including call volume, common caller questions, peak call times, conversion rates for appointment scheduling, and more to help optimize your practice.'
    },
    {
      question: 'Do you offer White Glove setup?',
      answer: 'Yes! We offer comprehensive White Glove setup service where our team handles the entire onboarding process for you, including phone system configuration, calendar integration, staff training, and custom workflow setup. Contact us for pricing.'
    },
    {
      question: 'Can you build a custom website for my practice?',
      answer: 'Absolutely! In addition to our standard website templates, we offer custom website design and development services tailored to your practice\'s unique brand and needs. Pricing is negotiable based on your specific requirements. Contact us to discuss your vision.'
    },
    {
      question: 'Do you provide staff training?',
      answer: `Yes! We offer comprehensive staff training services to ensure your team knows how to effectively use ${APP_NAME} and integrate it into your daily workflows. Training packages are available at negotiable rates based on your practice size and needs.`
    },
    {
      question: 'Can you help with domain transfers?',
      answer: 'Yes, we can assist with domain transfers from your current provider to ensure a smooth transition. This service is available for a one-time setup fee. Pricing is negotiable based on the complexity of your transfer. Contact us to get started.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* FAQ Schema for SEO */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqs.map(faq => ({
            "@type": "Question",
            "name": faq.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": faq.answer
            }
          }))
        })}
      </script>

      {/* Hero Section */}
      <div className="bg-gradient-to-b from-blue-50 to-white py-12 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex justify-center mb-4">
            <img 
              src="/logo512.png" 
              alt={`${APP_NAME} Logo`}
              className="w-24 h-24 object-contain"
            />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-3">
            Frequently Asked Questions
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Everything you need to know about {APP_NAME}'s AI Front Desk Agent
          </p>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl overflow-hidden border border-gray-200">
          {faqs.map((faq, index) => (
            <div key={index} className="border-b border-gray-100 last:border-b-0">
              <button
                className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
                onClick={() => {
                  setExpandedFaq(expandedFaq === index ? null : index);
                  if (expandedFaq !== index) {
                    trackFaqClick(faq.question);
                  }
                }}
              >
                <span className="text-lg font-medium text-gray-900 pr-8 leading-relaxed">
                  {faq.question}
                </span>
                <ArrowRight
                  className={`w-6 h-6 text-gray-400 transition-transform flex-shrink-0 ${
                    expandedFaq === index ? 'rotate-90' : ''
                  }`}
                />
              </button>
              {expandedFaq === index && (
                <div className="px-6 pb-6 pt-2 text-gray-600">
                  <p className="text-base leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-blue-600 py-12 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
            Still have questions?
          </h2>
          <p className="text-xl text-blue-100 mb-6">
            Our team is here to help you get started
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-3 bg-white text-blue-600 font-semibold rounded-lg hover:bg-blue-50 transition-colors"
            >
              Contact Us
            </Link>
            <Link
              to="/signup"
              className="inline-flex items-center justify-center px-8 py-3 bg-blue-700 text-white font-semibold rounded-lg hover:bg-blue-800 transition-colors border border-blue-500"
            >
              Get {APP_NAME} Today
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQ;

